import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpErrorResponse } from '@angular/common/http';
import { Observable, BehaviorSubject, throwError } from 'rxjs';
import { switchMap, catchError, finalize, filter, take } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { User } from 'src/app/models/user.model';
import { AuthenticationService } from '../authentication.service';

@Injectable()
export class JwtInterceptor implements HttpInterceptor {

  private readonly env = environment;
  private _isTokenRefreshing = false;
  private _tokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);


  constructor(
    private authService: AuthenticationService
  ) { }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

    // exclude login and refresh tokens requests and url using different auths.
    if (this.isRequestExcluded(request)) {
      return next.handle(request);
    }

    const user = this.authService.getCurrentUser();

    // not authenticated
    if (!user || !user.accessToken) {
      return throwError(() => new Error('No user information'));
    }

    return next.handle(this.cloneRequestWithHeaders(request, user.accessToken)).pipe(
      catchError((err): Observable<any> => {
        if (err instanceof HttpErrorResponse) {
          switch ((<HttpErrorResponse>err).status) {
            case 401:
              return this.handleRefreshToken(request, next);
            default:
              return throwError(() => err);
          }
        } else {
          return throwError(() => err);
        }
      })
    );
  }

  private cloneRequestWithHeaders(request: HttpRequest<any>, token: string | null) {
    // add authorization header with jwt token if available
    if (!token) {
      const currentUser = this.authService.getCurrentUser();
      if (currentUser && currentUser.accessToken) {
        token = currentUser.accessToken;
      }
    }

    request = request.clone({
      setHeaders: {
        'Authorization': `Bearer ${token}`,
        'X-USER-TYPE': this.env.xUserType
      }
    });

    return request;
  }

  private handleRefreshToken(req: HttpRequest<any>, next: HttpHandler): Observable<any> {
    if (!this._isTokenRefreshing) {
      // start refreshing
      this._isTokenRefreshing = true;
      // Reset here so that the following requests wait until the token comes back from the refreshToken Call.
      this._tokenSubject.next(null);
      // get new token
      return this.authService.refreshTokens().pipe(
        catchError(err => {
          this.authService.logout();
          return throwError(() => err);
        }),
        switchMap((user: User) => {
          if (user) {
            this._tokenSubject.next(user.accessToken);
            return next.handle(this.cloneRequestWithHeaders(req, user.accessToken));
          }
          // No new token
          this.authService.logout();
          return throwError(() => new Error('Refresh tokens had success, but the user values were not found.'));
        }),
        finalize(() => this._isTokenRefreshing = false)
      );
    } else {
      return this._tokenSubject.pipe(
        filter(token => token != null),
        take(1),
        switchMap((token) => next.handle(
          this.cloneRequestWithHeaders(req, token))
        )
      );
    }
  }

  private isRequestExcluded(request: HttpRequest<any>): boolean {
    let skip = false;
    const url = new URL(request.url);
    const pathname = url.pathname.toLowerCase();
    const host = url.host.toLowerCase();
    const internalAPIurl =  this.buildInternalApiURL();

    // let's skip all the requests not directed to internal API.
    if (host != internalAPIurl) {
      return true;
    }

    if (this.env.skipRefreshTokenUrls.includes(pathname.toLowerCase()) || request.headers.has('Skip')) {
      return true;
    }

    const excludedUrls = this.env.skipJwtUrlStartsWith;

    for (let index = 0; index < excludedUrls.length; index++) {
      const urlToSkip = excludedUrls[index];
      if (pathname.startsWith(urlToSkip)) {
        skip = true;
        break;
      }
    }

    return skip;
  }

  private buildInternalApiURL() {
    let internalApi = this.env.remote_host.toLowerCase();
    if (this.env.debugApiMode) {
      internalApi = `${this.env.host}${this.env.useHttps ? this.env.localHttpsPort : this.env.localHttpPort}`
    }
    return internalApi;
  }
}
